import React, { useState, useEffect } from "react";
import { XMarkIcon, ChevronLeftIcon, SparklesIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { adminApi } from "../../repositories/admin";
import { userApi } from "../../repositories/user";
import Button from "../ui/Button";

interface AppUpdate {
  id: number;
  title: string;
  updateData: string;
  views: number;
  status: "published" | "draft";
  createdAt: string;
  updatedAt: string;
}

interface UserAppUpdatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDismiss: (updateId: number) => void;
  userId: number;
  userRole: string;
}

const UserAppUpdatesModal: React.FC<UserAppUpdatesModalProps> = ({
  isOpen,
  onClose,
  onDismiss,
  userId,
  userRole,
}) => {
  const [appUpdates, setAppUpdates] = useState<AppUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<AppUpdate | null>(null);
  const [viewedIds, setViewedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchAppUpdates();
    }
  }, [isOpen]);

  const fetchAppUpdates = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAllAppUpdates({
        page: 1,
        limit: 100, 
        userRole: userRole,
     
      });
      if (response?.data?.success) {
        // Filter only published updates for users
        const publishedUpdates = (response.data.data || []).filter(
          (update: AppUpdate) => update.status === "published"
        );
        setAppUpdates(publishedUpdates);
      }
    } catch (error) {
      console.error("Failed to fetch app updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClick = async (update: AppUpdate) => {
    try {
      if(!viewedIds.has(update.id)){
        await adminApi.viewAppUpdate(update.id, userId);
        setViewedIds((prev) => new Set(prev).add(update.id));
      } 
      setSelectedUpdate(update);
    } catch (error) {
      console.error("Failed to track update view:", error);
      setSelectedUpdate(update);
    }
  };

  const handleDismiss = async (updateId: number) => {
    try {
      if(!viewedIds.has(updateId)){
        await userApi.dismissAppUpdate(userId, updateId);
      }
      setAppUpdates((prev) => prev.filter((update) => update.id !== updateId));
      onDismiss(updateId);
    } catch (error) {
      console.error("Failed to dismiss update:", error);
    }
  };

  const handleBackToList = () => {
    setSelectedUpdate(null);
  };

  if (!isOpen) return null;

  // Don't show modal if loading or if there are no app updates
  if (loading || appUpdates.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={() => {}}
    >
      <div
        className="relative flex flex-col w-full max-w-2xl max-h-[85vh] overflow-hidden bg-white shadow-2xl rounded-2xl ring-1 ring-black/5 animate-modalIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — gradient banner with icon */}
        <div className="relative px-6 py-5 overflow-hidden bg-gradient-to-r from-primary-700 via-primary-600 to-primary-800">
          {/* subtle decorative glow */}
          <div className="absolute rounded-full pointer-events-none -top-10 -right-6 w-36 h-36 bg-white/10 blur-2xl" />
          <div className="relative flex items-center justify-between gap-3">
            {selectedUpdate ? (
              <div className="flex items-center min-w-0 gap-3">
                <button
                  onClick={handleBackToList}
                  className="p-1.5 transition-colors rounded-lg text-white/90 hover:bg-white/15"
                  aria-label="Back to list"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-semibold text-white truncate">
                  {selectedUpdate.title}
                </h2>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/15">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold leading-tight text-white">What's New in Cadence</h2>
                  <p className="text-xs text-white/70">The latest features and improvements</p>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 transition-colors border border-transparent rounded-lg text-white/80 hover:text-white hover:bg-white/15 focus:outline-none focus-visible:border-white/60"
              aria-label="Close modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-neutral-50">
          {selectedUpdate ? (
            // Update Detail View
            <div className="p-6">
              <div
                className="p-5 prose bg-white border shadow-sm max-w-none rounded-xl border-neutral-200"
                dangerouslySetInnerHTML={{ __html: selectedUpdate.updateData }}
              />
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => {
                    handleDismiss(selectedUpdate.id);
                    handleBackToList();
                  }}
                >
                  Got it
                </Button>
              </div>
            </div>
          ) : (
            // Updates List View
            <div className="p-6">
              {appUpdates.length > 0 ? (
                <div className="space-y-3">
                  {appUpdates.map((update) => (
                    <div
                      key={update.id}
                      className="group flex items-center justify-between gap-4 p-4 transition-all bg-white border shadow-sm border-neutral-200 rounded-xl hover:shadow-md hover:border-primary-300 hover:-translate-y-0.5"
                    >
                      <button
                        onClick={() => handleUpdateClick(update)}
                        className="flex items-center flex-1 min-w-0 gap-3 text-left"
                      >
                        <span className="flex items-center justify-center w-10 h-10 transition-colors rounded-lg shrink-0 bg-primary-50 text-primary-700 group-hover:bg-primary-100">
                          <SparklesIcon className="w-5 h-5" />
                        </span>
                        <span className="min-w-0">
                          <h3 className="text-base font-semibold text-black truncate transition-colors group-hover:text-primary-700">
                            {update.title}
                          </h3>
                          <p className="text-sm text-neutral-500">
                            {new Date(update.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        </span>
                        <ChevronRightIcon className="w-5 h-5 ml-auto transition-colors text-neutral-300 shrink-0 group-hover:text-primary-500" />
                      </button>
                      <Button
                        variant="neutral"
                        onClick={() => handleDismiss(update.id)}
                        className="shrink-0"
                      >
                        Got it
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <p className="text-neutral-500">No app updates available.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAppUpdatesModal;

